import { ArrowRight, EyeOff, Hand, KeyRound, LogIn, MonitorUp, Play, ShieldCheck, UserCheck } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import { LIMITS, type CreateMeetingResponse, type MyMeeting } from '@amg/protocol';
import { AppHeader, ChangePasswordDialog } from '../components/AppHeader';
import { JoinForm } from '../components/JoinForm';
import { Button } from '../components/ui/Button';
import { Confirm } from '../components/ui/Dialog';
import { Field, FormError, Input } from '../components/ui/Field';
import { Spinner } from '../components/ui/Spinner';
import { api, ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ago, firstName } from '../lib/format';
import { navigate } from '../router';

export function Home() {
  const { user, loading } = useAuth();
  const goToMeeting = (id: string) => navigate(`/m/${id}`);

  return (
    <div className="flex min-h-dvh flex-col">
      <AppHeader />
      <main id="main" className="flex-1">
        {loading ? (
          <div className="grid min-h-[60dvh] place-items-center">
            <Spinner className="size-5 text-ink-3" label="Loading" />
          </div>
        ) : user ? (
          <section className="mx-auto grid max-w-6xl gap-x-16 gap-y-12 px-4 pb-20 pt-10 sm:px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:pt-16">
            <div className="animate-rise">
              <Eyebrow>Signed in as {firstName(user.name)}</Eyebrow>
              <h1 className="font-display text-display text-ink">Start a support session.</h1>
              <p className="mt-5 max-w-[34rem] text-[1.0625rem] leading-relaxed text-ink-3">
                You'll get a code to read out or send. Everyone who uses it waits until you let them in.
              </p>
              <StartSessionCard mustChangePassword={user.mustChangePassword} onStarted={goToMeeting} />
              <OpenSessions />
            </div>
            <aside className="animate-rise [animation-delay:60ms] lg:pt-3">
              <section aria-labelledby="join-colleague" className="rounded-xl border border-line bg-surface p-6 shadow-xs sm:p-7">
                <h2 id="join-colleague" className="text-heading font-semibold">
                  Join someone else's session
                </h2>
                <p className="mb-6 mt-1 text-compact text-ink-3">Got a code from a colleague? Enter it here.</p>
                <JoinForm onJoined={goToMeeting} submitVariant="secondary" />
              </section>
            </aside>
          </section>
        ) : (
          <section className="mx-auto grid max-w-6xl gap-x-16 gap-y-14 px-4 pb-20 pt-10 sm:px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:pt-16">
            <div className="animate-rise">
              <Eyebrow>AMG remote support</Eyebrow>
              <h1 className="font-display text-display text-ink">Get help, right on your screen.</h1>
              <p className="mt-5 max-w-[34rem] text-[1.0625rem] leading-relaxed text-ink-3">
                Enter the code from the person helping you. They let you in, and nothing on your computer is shared until you choose.
              </p>
              <section aria-label="Join a session" className="mt-9 max-w-[30rem] rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-7">
                <JoinForm onJoined={goToMeeting} autoFocus={window.matchMedia('(pointer: fine)').matches} />
                <p className="mt-5 text-meta text-ink-3">No code yet? Ask your AMG contact to start a session and give you one.</p>
              </section>
            </div>
            <aside className="animate-rise [animation-delay:60ms] lg:pt-4">
              <HowItWorks />
              <div className="mt-10 border-t border-line pt-8">
                <h2 className="text-heading font-semibold">For AMG staff</h2>
                <p className="mt-1 max-w-sm text-compact text-ink-3">Sign in to start a session and invite the people you're helping.</p>
                <Button variant="secondary" size="md" className="mt-5" icon={<LogIn aria-hidden className="size-4" />} onClick={() => navigate('/signin')}>
                  Sign in to start a session
                </Button>
              </div>
            </aside>
          </section>
        )}
        <TrustNotes />
      </main>
      <footer className="border-t border-line/70">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-meta text-ink-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>© {new Date().getFullYear()} AMG</p>
          <p>{window.location.host} · Remote support for AMG customers and staff</p>
        </div>
      </footer>
    </div>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-meta font-semibold uppercase tracking-[0.14em] text-accent-text">{children}</p>;
}

const steps: { title: string; body: string; icon: ReactNode }[] = [
  { title: 'Get a code', body: 'The person helping you starts a session and gives you a 10‑character code.', icon: <KeyRound aria-hidden /> },
  { title: 'Wait to be let in', body: 'You join a short waiting room. They can see your name before admitting you.', icon: <UserCheck aria-hidden /> },
  { title: 'Share only if you choose', body: 'Pick one screen, window or tab to show. Stop at any moment.', icon: <MonitorUp aria-hidden /> },
];

function HowItWorks() {
  return (
    <section aria-labelledby="how-heading">
      <h2 id="how-heading" className="text-heading font-semibold">
        How it works
      </h2>
      <ol className="mt-6 flex flex-col">
        {steps.map((s, i) => (
          <li key={s.title} className="relative flex gap-4 pb-7 last:pb-0">
            {i < steps.length - 1 && <span aria-hidden className="absolute left-[1.1875rem] top-11 bottom-2 w-px bg-line-strong" />}
            <span className="grid size-10 shrink-0 place-items-center rounded-full border border-line-strong bg-surface text-accent-text shadow-xs [&_svg]:size-[1.125rem]">
              {s.icon}
            </span>
            <div className="pt-1.5">
              <h3 className="text-compact font-semibold text-ink">
                <span className="sr-only">Step {i + 1}: </span>
                {s.title}
              </h3>
              <p className="mt-0.5 max-w-sm text-compact text-ink-3">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function TrustNotes() {
  const notes: { icon: ReactNode; title: string; body: string }[] = [
    { icon: <Hand aria-hidden />, title: 'You stay in control', body: 'Your browser asks what to share. The browser shows when sharing is on, and Stop is always one click away.' },
    { icon: <ShieldCheck aria-hidden />, title: 'Nobody gets in uninvited', body: 'Codes expire, and the host approves every person by name before they can see anything.' },
    { icon: <EyeOff aria-hidden />, title: 'Nothing is recorded', body: 'Screens travel between participants’ browsers, encrypted in transit. A relay, if needed, only passes encrypted data along.' },
  ];
  return (
    <section aria-label="Privacy and safety" className="border-t border-line/70 bg-surface/50">
      <ul className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-3 md:gap-10">
        {notes.map((n) => (
          <li key={n.title} className="flex gap-3.5">
            <span className="mt-0.5 text-ink-3 [&_svg]:size-5">{n.icon}</span>
            <div>
              <h3 className="text-compact font-semibold text-ink">{n.title}</h3>
              <p className="mt-1 text-compact text-ink-3">{n.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StartSessionCard({ mustChangePassword, onStarted }: { mustChangePassword: boolean; onStarted: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<CreateMeetingResponse>('/api/meetings', { body: { title: title.trim() || undefined } });
      onStarted(res.meetingId);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'password_change_required') setPasswordOpen(true);
      else setError(errorMessage(err));
      setBusy(false);
    }
  };

  if (mustChangePassword) {
    return (
      <div className="mt-9 max-w-[30rem] rounded-xl border border-warning/30 bg-warning-soft p-6">
        <h2 className="text-heading font-semibold text-ink">Choose your own password first</h2>
        <p className="mt-1 text-compact text-ink-2">Your account still uses the one-time password from your administrator.</p>
        <Button variant="primary" className="mt-5" icon={<KeyRound aria-hidden className="size-4" />} onClick={() => setPasswordOpen(true)}>
          Set a new password
        </Button>
        <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
      </div>
    );
  }

  return (
    <form onSubmit={start} className="mt-9 flex max-w-[30rem] flex-col gap-5 rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-7" noValidate>
      <FormError>{error}</FormError>
      <Field label="What's it about?" hint="Optional. Shown to people while they wait, e.g. “Printer setup for Rahul”.">
        {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={LIMITS.titleMax} placeholder="Support session" />}
      </Field>
      <Button type="submit" variant="primary" size="xl" block loading={busy} icon={busy ? undefined : <Play aria-hidden className="size-4 fill-current" />}>
        {busy ? 'Starting…' : 'Start session'}
      </Button>
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </form>
  );
}

function OpenSessions() {
  const [meetings, setMeetings] = useState<MyMeeting[] | null>(null);
  const [ending, setEnding] = useState<MyMeeting | null>(null);
  const [resuming, setResuming] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ meetings: MyMeeting[] }>('/api/meetings/mine');
      setMeetings(res.meetings);
    } catch {
      setMeetings([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!meetings || meetings.length === 0) return null;

  const resume = async (id: string) => {
    setResuming(id);
    try {
      await api(`/api/meetings/${id}/resume`, { body: {} });
      navigate(`/m/${id}`);
    } catch (err) {
      toast.error(errorMessage(err));
      setResuming(null);
      void load();
    }
  };

  const end = async (m: MyMeeting) => {
    try {
      await api(`/api/meetings/${m.id}/end`, { body: {} });
      toast.success(`Ended “${m.title}”`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
    void load();
  };

  return (
    <section aria-labelledby="open-heading" className="mt-10 max-w-[30rem]">
      <h2 id="open-heading" className="text-label font-semibold uppercase tracking-[0.08em] text-ink-3">
        Your open sessions
      </h2>
      <ul className="mt-3 divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
        {meetings.map((m) => (
          <li key={m.id} className="flex items-center gap-3 px-4 py-3">
            <span className="relative flex size-2.5 shrink-0" aria-hidden>
              <span className="absolute inset-0 animate-live-ring rounded-full bg-success" />
              <span className="relative size-2.5 rounded-full bg-success" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-compact font-medium text-ink">{m.title}</p>
              <p className="text-meta text-ink-3">
                Started {ago(m.createdAt)} · {m.participantCount} {m.participantCount === 1 ? 'person' : 'people'}
              </p>
            </div>
            <Button size="sm" variant="ghost" className="text-danger-text hover:bg-danger-soft hover:text-danger-text" onClick={() => setEnding(m)}>
              End
            </Button>
            <Button size="sm" variant="secondary" loading={resuming === m.id} iconRight={<ArrowRight aria-hidden className="size-3.5" />} onClick={() => void resume(m.id)}>
              Open
            </Button>
          </li>
        ))}
      </ul>
      <Confirm
        open={ending !== null}
        onOpenChange={(open) => !open && setEnding(null)}
        title="End this session?"
        description={`Everyone in “${ending?.title ?? ''}” will be disconnected and the code will stop working.`}
        confirmLabel="End session"
        onConfirm={() => ending && void end(ending)}
      />
      <p className="mt-2 text-meta text-ink-3">Sessions end on their own if you stay away for a while.</p>
    </section>
  );
}
